<template>
  <v-row no-gutters>
    <v-col cols="12">
      <template v-if="isFetched">
        <feature-card-basic v-if="featureWithoutTree" :layer="layer" @featureEdited="init" />
        <feature-card-basic
          v-else
          ref="featureCard"
          :tree-view-feature="currentFeature"
          :layer="layer"
          :title="title"
          :update-tree-view-after-edit="init"
          @update:feature="setFeature"
          @update:isEditingOn="setIsEditingOn"
          @cancelTreeViewElementAdding="cancelTreeViewElementAdding"
        >
          <template #top>
            <slot
              name="treeview-top"
              v-bind="{ mainLayerName: treeFeature.metadata.layer, mainLayerFeatureId: treeFeature.id }"
            />
            <sidebar-row class="pt-3 pb-5 pr-5" :border-bottom="true">
              <feature-treeview
                ref="feature-treeview"
                v-model="activeFeature"
                :is-editing-on="isEditingOn"
                :treeFeature="[treeFeature]"
                v-on="treeViewListeners"
              >
                <template v-for="(value, key) in $scopedSlots" #[key]="scope">
                  <slot :name="key" v-bind="scope" />
                </template>
              </feature-treeview>
            </sidebar-row>
          </template>
        </feature-card-basic>
      </template>
      <main-loader v-else class="pt-10" />
      <main-dialog
        :is-visible.sync="isDeleteDialogVisible"
        v-if="isDeleteDialogLoaded"
        :body="deleteDialogBody"
        :loading="isDeleteDialogLoading"
        :title="deleteDialogTitle"
        agree-text="dialog.agreeDelete"
        @agree="deleteObjectHandler"
      />
      <main-input-dialog
        :is-visible.sync="isAddDialogVisible"
        v-if="isAddDialogLoaded"
        :title-translation="addDialogTitle"
        agree-text="dialog.agreeAdd"
        @agree="addObjectHandler"
        :inputs="addDialogInputs"
      />
    </v-col>
  </v-row>
</template>

<script>
import { call, get } from 'vuex-pathify';
import fetchAttributesSchemaMixin from '@/mixins/fetchAttributesSchemaMixin';
import prepareAttributesFeatureCardMixin from '@/mixins/prepareAttributesFeatureCardMixin';
import FeatureCardBasic from '@/components/FeatureCardBasic.vue';
import FeatureTreeview from '@/components/FeatureTreeview.vue';
import MainLoader from '@/components/MainLoader.vue';
import SidebarRow from '@/components/SidebarRow.vue';
export default {
  name: 'FeatureCardTreeview',
  mixins: [fetchAttributesSchemaMixin, prepareAttributesFeatureCardMixin],
  components: {
    FeatureCardBasic,
    FeatureTreeview,
    MainDialog: () => import('@/components/MainDialog'),
    MainInputDialog: () => import('@/components/MainInputDialog'),
    MainLoader,
    SidebarRow,
  },
  props: {
    layer: {
      type: Object,
      required: true,
    },
    cardTitleBasedOnTreeElement: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    dataSourcesAll: get('layers/metadata'),
    layersAll: get('layers/layers'),
    treeViewMetadata: get('attributesSchema/treeViewMetadata@:lid'),
    layersAllArray() {
      return this.$_objectToArray(this.layersAll);
    },
    addDialogInputs() {
      return [
        {
          name: 'type',
          value: '',
          translationPath: 'dialog.objectType',
          cols: '12',
          rules: [v => !!v || v == '0' || 'rules.required'],
          dataType: {
            name: 'select',
          },
          items: this.parentItemToAddLayers,
          itemText: 'title',
          itemValue: 'layer',
        },
      ];
    },
    addDialogTitle() {
      return this.$i18n.t('dialog.title.addNewObject');
    },
    deleteDialogBody() {
      return this.$i18n.t('dialog.body.deleteObject', { id: this.itemToDelete.id });
    },
    deleteDialogTitle() {
      return this.$i18n.t('dialog.title.deleteObject');
    },
    flatLidFidsValues() {
      return Object.keys(this.flatTreeFeatureValues);
    },
    layersLengths() {
      const layersLengths = {};
      for (const lidFid of this.flatLidFidsValues) {
        const [lid] = lidFid.split('@');
        if (!layersLengths[lid]) {
          layersLengths[lid] = 1;
        } else {
          layersLengths[lid]++;
        }
      }
      return layersLengths;
    },
    computedLidFid() {
      return { lid: this.lid, fid: this.fid };
    },
    fid() {
      return this.routeParams.fid;
    },
    lid() {
      return this.routeParams.lid;
    },
    treeViewListeners() {
      const listeners = {
        ...this.$listeners,
        deleteTreeViewElement: this.deleteTreeViewElement,
        editTreeViewElement: this.editTreeViewElement,
        addNewObjectTreeViewElement: this.addTreeViewElement,
      };
      return listeners;
    },
    routeQuery() {
      return this.$route.query;
    },
    routeParams() {
      return this.$route.params;
    },
    title() {
      const mainFeauteDescAttribute = this.metadata[this.treeFeature.metadata.datasource]?.attributes_schema
        .desc_attribute_name;
      const values = {
        ...this.getFlattenProperties(this.treeFeature, mainFeauteDescAttribute),
        id: this.treeFeature.id,
      };
      if (this.cardTitleBasedOnTreeElement) {
        const mainFeatureTitle = this.treeFeature?.metadata?.title;
        return mainFeatureTitle && mainFeauteDescAttribute
          ? `${mainFeatureTitle}: ${values[mainFeauteDescAttribute]}`
          : '';
      } else {
        return {
          title: values[mainFeauteDescAttribute] || '',
          subtitle: this.layersAll[this.treeFeature.metadata.layer]?.name || '',
        };
      }
    },
  },
  data: () => ({
    activeFeature: '',
    attributesSchemaKeys: [],
    currentFeature: undefined,
    flatTreeFeatureValues: {},
    isDeleteDialogLoaded: false,
    isDeleteDialogLoading: false,
    isDeleteDialogVisible: false,
    isAddDialogLoaded: false,
    isAddDialogLoading: false,
    isAddDialogVisible: false,
    isEditingOn: false,
    isFetched: false,
    itemToDelete: undefined,
    parentItemToAdd: undefined,
    parentItemToAddLayers: [],
    treeFeature: undefined,
    treeMetadata: undefined,
    featureWithoutTree: false,
  }),
  methods: {
    deleteDatasourceFeature: call('layers/deleteDatasourceFeature'),
    getTreeFeature: call('attributesSchema/getTreeFeature'),
    getTreeMetadata: call('attributesSchema/getTreeMetadata'),
    addTreeViewElement(element) {
      const elementLayer = element.metadata.layer;
      const elementLayerMetadata = this.treeMetadata[elementLayer];
      const layersToAdd = elementLayerMetadata.children?.filter(children => {
        const { max_occurance: max } = children;
        const childrenLayerLength = this.layersLengths[children.layer];
        if (!childrenLayerLength) {
          return true;
        }
        return !max || max > childrenLayerLength;
      });
      this.parentItemToAdd = element;
      this.parentItemToAddLayers = layersToAdd;

      this.isAddDialogLoaded = true;
      this.isAddDialogVisible = true;
    },
    cancelTreeViewElementAdding() {
      this.treeFeature = this.deleteNewTreeViewElements(this.treeFeature);
      this.activeFeature = `${this.lid}@${this.fid}`;
    },
    deleteNewTreeViewElements(element) {
      return element.children
        ? {
            ...element,
            children: element.children
              .filter(children => children.id !== 'new')
              .map(ch => this.deleteNewTreeViewElements(ch)),
          }
        : { ...element };
    },
    deleteTreeViewElement(item) {
      this.itemToDelete = item;
      this.isDeleteDialogLoaded = true;
      this.isDeleteDialogVisible = true;
    },
    editTreeViewElement(item) {
      if (item.treeKey !== this.activeFeature) {
        this.activeFeature = item.treeKey;
      }
      setTimeout(() => {
        this.$refs.featureCard.startEditing('edit');
      });
    },
    getElementTreeKey(element, lid) {
      return `${lid}@${element.id}`;
    },
    getTreeFeatureElement(element, crs) {
      const { datasource, layer, badges, subtitle } = element.metadata;
      const idAttributeName = this.dataSourcesAll[this.layersAll[layer]?.data_source_name]?.attributes_schema.id_name;
      if (!this.attributesSchemaKeys.find(attSch => attSch.layerId == layer && attSch.dataSourceName == datasource)) {
        this.attributesSchemaKeys.push({ dataSourceName: datasource, layerId: layer });
      }

      element.crs = element.crs || crs;
      this.flatTreeFeatureValues[`${layer}@${element.id}`] = element;

      const mappedBadges = badges
        ?.map(badge => {
          const { badge_color: color, badge_color_attribute: attribute } = badge;
          let { badge_text: text } = badge;
          text = this.getQueryValue(text, { ...element.properties, [idAttributeName]: element.id });

          if (attribute) {
            const badgeAttributeMetadata =
              this.dataSourcesAll[datasource]?.attributes_schema.attributes.find(attr => attr.id === attribute) || {};
            const { name, dict_colors: dictColors, allowed_values: dictValues } = badgeAttributeMetadata;
            const index = dictValues.indexOf(element.properties[name]);
            return { text, color: dictColors[index] };
          }
          return { text, color };
        })
        .filter(badge => !!badge.text);
      const mappedSubtitle = this.getQueryValue(subtitle, { ...element.properties, [idAttributeName]: element.id });

      return element.children
        ? {
            ...element,
            metadata: { ...element.metadata, badges: mappedBadges, subtitle: mappedSubtitle },
            treeKey: this.getElementTreeKey(element, layer),
            children: element.children.map(ch => this.getTreeFeatureElement(ch, element.crs)),
          }
        : {
            ...element,
            metadata: { ...element.metadata, badges: mappedBadges, subtitle: mappedSubtitle },
            treeKey: this.getElementTreeKey(element, layer),
          };
    },
    getQueryValue(queryValue, values = {}) {
      const queryVars = [];
      const rxp = /{([^}]+)}/g;
      let curMatch;
      while ((curMatch = rxp.exec(queryValue))) {
        queryVars.push(curMatch[1]);
      }
      for (const queryVar of queryVars) {
        const elementValue = values[queryVar];
        queryValue = queryValue.replace(
          new RegExp('{' + queryVar + '}', 'gi'),
          elementValue || elementValue === 0 ? elementValue : ''
        );
      }
      return queryValue;
    },
    getTreeFeatureElementDotsItems(element) {
      element.dotsItems = this.getDotsItems(element);
      return element.children
        ? {
            ...element,
            children: element.children.map(ch => this.getTreeFeatureElementDotsItems(ch)),
          }
        : { ...element };
    },
    getTreeMetadataElements(treeMetadata = this.treeViewMetadata) {
      treeMetadata = treeMetadata.map(treeM => {
        const treeMId = treeM.id;
        const children = treeMetadata.filter(treeM => treeM.parent_id === treeMId);
        return { ...treeM, children };
      });
      return this.$_arrayToObject(treeMetadata, 'layer');
    },
    getDotsItems(element) {
      const elementLayer = element.metadata.layer;
      const elementLayerMetadata = this.treeMetadata[elementLayer];
      const { min_occurance: min } = elementLayerMetadata;
      const deleteDisabled = Boolean(min && min >= this.layersLengths[elementLayer]);
      const layersToAdd = elementLayerMetadata.children?.filter(children => {
        const { max_occurance: max } = children;
        const childrenLayerLength = this.layersLengths[children.layer];
        if (!childrenLayerLength) {
          return true;
        }
        return !max || max > childrenLayerLength;
      });
      const hasAdd = Boolean(layersToAdd?.length);
      return [
        ...(hasAdd ? [{ name: 'addNewObject' }] : []),
        {
          name: 'edit',
        },
        {
          name: 'delete',
          disabled: deleteDisabled,
          tooltip: deleteDisabled
            ? this.$i18n.t('rules.minObjectsTypeOccurrence', { type: elementLayerMetadata.title, value: min })
            : '',
        },
      ];
    },
    goBack() {
      this.$router.back();
    },
    async pushToParentRoute() {
      await this.$router.push({
        name: this.$route.meta.parent,
        params: {
          ...this.routeParams,
        },
      });
      this.currentTab = 0;
    },
    refreshTableAndMap(layer = this.lid) {
      this.$root.$emit('refreshTableData');
      this.$root.$emit('refreshMvtSource', layer);
      this.$root.$emit('updateLegendStylesCounts', { layersIds: [layer] });
    },
    setActiveFeatureData(lid, fid) {
      this.currentFeature = this.flatTreeFeatureValues[`${lid}@${fid}`];
    },
    setFeature(feature) {
      this.$emit('update:feature', feature);
    },
    setIsEditingOn(value) {
      this.isEditingOn = value;
      this.$emit('update:isEditingOn', value);
    },
    async addObjectHandler() {
      this.isAddDialogLoading = true;
      const values = this.$_arrayToObject(this.addDialogInputs, 'name', 'value');
      const newObjectMetadata = this.treeMetadata[values.type];
      const { datasource, layer, title, child_attribute } = newObjectMetadata;
      const properties = { [child_attribute]: this.parentItemToAdd.id };
      const newObject = {
        children: [],
        crs: this.treeFeature.crs,
        geometry: this.treeFeature.geometry,
        id: 'new',
        metadata: {
          badge: null,
          badge_color: null,
          datasource,
          layer,
          title,
          subtitle: `(${this.$i18n.t('sidebar.newObject')})`,
        },
        properties,
        treeKey: `${layer}@new`,
        type: 'Feature',
      };
      this.treeFeature.children.push(newObject);
      this.flatTreeFeatureValues[`${layer}@new`] = newObject;

      this.activeFeature = newObject.treeKey;
      this.isAddDialogVisible = false;
      this.isAddDialogLoading = false;
      this.$refs['feature-treeview']?.$refs['f-treeview']?.updateAll(true);
    },
    async deleteObjectHandler() {
      try {
        this.isDeleteDialogLoading = true;
        const datasource = this.itemToDelete.metadata.datasource;
        const fid = this.itemToDelete.id;
        const layer_id = this.itemToDelete.metadata.layer;
        await this.deleteDatasourceFeature({ datasource, id: fid, params: { cascade: true, layer_id } });
        this.refreshTableAndMap(this.itemToDelete.metadata.layer);
        const mainFeatureDataSource = this.treeFeature?.metadata?.datasource;
        const currentFeatureDataSource = this.currentFeature?.metadata?.datasource;
        if (datasource === mainFeatureDataSource) {
          await this.pushToParentRoute();
        } else if (datasource === currentFeatureDataSource) {
          this.activeFeature = this.treeFeature.treeKey;
          await this.init(true);
        } else {
          await this.init(true);
        }
        this.isDeleteDialogLoading = false;
        this.isDeleteDialogVisible = false;
      } catch (error) {
        this.isDeleteDialogVisible = false;
        this.isDeleteDialogLoading = false;
      }
    },
    async init(refresh = false, afterNewObjectAdded = false) {
      this.featureWithoutTree = false;
      try {
        if (!refresh) this.isFetched = false;
        const { id: layerId } = this.layer;
        const [treeFeature] = await Promise.all([
          this.getTreeFeature({ layerId, fid: this.fid }),
          ...(this.treeViewMetadata ? [] : [this.getTreeMetadata({ layerId })]),
        ]);
        this.attributesSchemaKeys = [];
        this.flatTreeFeatureValues = {};
        // Get tree metadata
        this.treeMetadata = this.getTreeMetadataElements();
        // Get mapped feature elements, flat structure and objects counts first
        this.treeFeature = this.getTreeFeatureElement(treeFeature);
        // Then get dots menus based on objects counts
        this.treeFeature = this.getTreeFeatureElementDotsItems(this.treeFeature);
        this.isFetched = true;
        if (refresh) {
          const [lid, fid] = this.activeFeature.split('@');
          this.setActiveFeatureData(lid, fid);
        } else if (afterNewObjectAdded) {
          const { lid, fid } = this.routeQuery;
          this.activeFeature = `${lid}@${fid}`;
        } else {
          this.activeFeature = `${this.lid}@${this.fid}`;
        }
        this.$forceUpdate();
      } catch (e) {
        if (e.status === 404 && e.rawMessage === 'DataSourceError.FeatureDoesNotExist') {
          this.featureWithoutTree = true;
          this.isFetched = true;
          console.log(`%cFeature not assigned to any tree`, 'color: red');
        }
      }
    },
  },
  watch: {
    activeFeature(nV) {
      const [lid, fid] = nV.split('@');
      this.setActiveFeatureData(lid, fid);
      this.$router.push({
        ...this.$route,
        query: {
          ...this.routeQuery,
          lid,
          fid,
        },
      });
    },
    computedLidFid: {
      immediate: false,
      deep: true,
      handler(oV, nV) {
        if (oV?.fid === nV?.fid && oV?.lid === nV?.lid) {
          return;
        }
        this.init();
      },
    },
  },
  mounted() {
    this.init();
  },
};
</script>
